import { useEffect, useState } from "react";
import { X, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Floating banner shown ONLY when:
 *   - the visitor's browser indicates French preference, AND
 *   - the site is currently displaying English, AND
 *   - the visitor has not dismissed the banner before.
 *
 * One-time, polite, no popup overlay — just a slim bar in the bottom-right.
 */
const STORAGE_KEY = "karine_fr_suggestion_dismissed";

const browserPrefersFrench = () => {
  try {
    const candidates = [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
    ].filter(Boolean);
    return candidates.some((c) => String(c).toLowerCase().startsWith("fr"));
  } catch {
    return false;
  }
};

const FrenchSuggestionBanner = () => {
  const { language, setLanguage } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (language !== "en") return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    if (!browserPrefersFrench()) return;
    // Small delay so it doesn't interrupt the hero animation
    const t = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(t);
  }, [language]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const switchToFrench = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setLanguage("fr");
    setVisible(false);
  };

  if (!visible || language !== "en") return null;

  return (
    <div
      className="fixed bottom-20 md:bottom-6 right-4 z-[10000] max-w-sm animate-in slide-in-from-bottom-5 fade-in duration-500"
      data-testid="french-suggestion-banner"
    >
      <div className="bg-gradient-to-br from-amber-50 to-stone-50 border border-amber-300 shadow-2xl rounded-xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-md">
          <Globe className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-semibold text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Vous parlez français&nbsp;?
          </p>
          <p className="text-xs text-stone-600 mt-0.5">
            Nous aussi&nbsp;! Cliquez ici pour le site en français.
          </p>
          <button
            type="button"
            onClick={switchToFrench}
            data-testid="french-suggestion-accept"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white transition-colors"
          >
            Passer au français →
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="french-suggestion-dismiss"
          className="text-stone-400 hover:text-stone-700 transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default FrenchSuggestionBanner;
